import { ScaffoldModule, Question, ScaffoldConfig, InstallResult, FileChange, FileOperations, Framework } from '@scaffit/core';
import { join } from 'path';


// CLI Integration (ScaffoldModule)
const scaffold: ScaffoldModule = {
  name: 'jest',
  description: 'Jest unit testing setup with framework-specific configurations',
  category: 'Testing',
  tags: ['testing', 'jest', 'unit-testing', 'test-runner', 'coverage', 'mocking'],

  prompts: async (): Promise<Question[]> => [
    {
      type: 'confirm',
      name: 'includeCoverage',
      message: 'Include coverage reporting?',
      default: true
    },
    {
      type: 'confirm',
      name: 'includeSetupFiles',
      message: 'Include test setup files?',
      default: true
    },
    {
      type: 'multiselect',
      name: 'features',
      message: 'Select additional features:',
      choices: [
        'typescript',
        'eslint',
        'mocks',
        'matchers',
        'utils'
      ],
      default: ['typescript', 'eslint', 'mocks', 'utils']
    }
  ],

  install: async (config: ScaffoldConfig): Promise<InstallResult> => {
    const result: InstallResult = {
      success: true,
      filesChanged: [],
      dependencies: {
        dev: [],
        prod: []
      },
      errors: [],
      warnings: []
    };

    try {
      const projectRoot = config.projectInfo?.rootDir || process.cwd();
      const framework = await FileOperations.detectFramework(projectRoot);
      const features = config.features || [];

      // Create jest.config.js file
      const jestConfigPath = join(projectRoot, 'jest.config.js');
      const isESMProject = await FileOperations.isESMProject(projectRoot);
      const jestConfig = generateJestConfig(framework, features, config.includeCoverage, isESMProject);

      await FileOperations.writeFile(jestConfigPath, jestConfig);
      result.filesChanged.push({
        path: 'jest.config.js',
        action: 'created',
        content: jestConfig
      });

      // Create test setup file if requested
      if (config.includeSetupFiles) {
        const setupPath = join(projectRoot, 'test', 'setup.ts');
        const setupContent = generateSetupFile(framework, features);

        // Ensure test directory exists
        const testDir = join(projectRoot, 'test');
        if (!(await FileOperations.fileExists(testDir))) {
          // Create directory using dynamic import
          const { mkdirSync } = await import('fs');
          mkdirSync(testDir, { recursive: true });
        }

        await FileOperations.writeFile(setupPath, setupContent);
        result.filesChanged.push({
          path: 'test/setup.ts',
          action: 'created',
          content: setupContent
        });
      }

      // Create example test file
      const exampleTestPath = join(projectRoot, 'test', 'example.test.ts');
      const exampleTestContent = generateExampleTest(framework);

      // Ensure test directory exists
      const testDir = join(projectRoot, 'test');
      if (!(await FileOperations.fileExists(testDir))) {
        // Create directory using dynamic import
        const { mkdirSync } = await import('fs');
        mkdirSync(testDir, { recursive: true });
      }

      await FileOperations.writeFile(exampleTestPath, exampleTestContent);
      result.filesChanged.push({
        path: 'test/example.test.ts',
        action: 'created',
        content: exampleTestContent
      });

      // Create test utilities if requested
      if (features.includes('utils')) {
        const utilsPath = join(projectRoot, 'test', 'utils.ts');
        const utilsContent = generateTestUtils(framework);

        await FileOperations.writeFile(utilsPath, utilsContent);
        result.filesChanged.push({
          path: 'test/utils.ts',
          action: 'created',
          content: utilsContent
        });
      }

      // Create mock utilities if requested
      if (features.includes('mocks')) {
        const mocksPath = join(projectRoot, 'test', 'mocks.ts');
        const mocksContent = generateMockUtils(framework);

        await FileOperations.writeFile(mocksPath, mocksContent);
        result.filesChanged.push({
          path: 'test/mocks.ts',
          action: 'created',
          content: mocksContent
        });
      }

      // Update package.json
      const packageJsonPath = join(projectRoot, 'package.json');
      if (await FileOperations.fileExists(packageJsonPath)) {
        const packageJsonContent = await FileOperations.readFile(packageJsonPath);
        if (!packageJsonContent) {
          result.errors.push('Failed to read package.json');
          return result;
        }
        
        const packageJson = JSON.parse(packageJsonContent);
        
        // Add dependencies
        if (!packageJson.devDependencies) {
          packageJson.devDependencies = {};
        }
        
        const dependencies = ['jest', '@types/jest'];
        
        // Framework-specific dependencies
        if (framework === 'nextjs') {
          dependencies.push('@testing-library/react', '@testing-library/jest-dom');
        } else if (framework === 'react-vite' || framework === 'react-cra') {
          dependencies.push('@testing-library/react', '@testing-library/jest-dom', '@testing-library/user-event');
        } else if (framework === 'vue') {
          dependencies.push('@vue/test-utils', '@testing-library/vue');
        } else if (framework === 'angular') {
          dependencies.push('@angular/testing', '@testing-library/angular');
        } else if (framework === 'svelte') {
          dependencies.push('@testing-library/svelte');
        }

        // Feature-specific dependencies
        if (features.includes('typescript')) {
          dependencies.push('ts-jest');
        }

        dependencies.forEach(dep => {
          if (!packageJson.devDependencies[dep]) {
            packageJson.devDependencies[dep] = 'latest';
            result.dependencies.dev.push(dep);
          }
        });

        // Add scripts
        if (!packageJson.scripts) {
          packageJson.scripts = {};
        }
        
        if (!packageJson.scripts.test) {
          packageJson.scripts.test = 'jest';
        }
        if (!packageJson.scripts['test:watch']) {
          packageJson.scripts['test:watch'] = 'jest --watch';
        }
        if (!packageJson.scripts['test:coverage']) {
          packageJson.scripts['test:coverage'] = 'jest --coverage';
        }

        await FileOperations.writeFile(packageJsonPath, JSON.stringify(packageJson, null, 2));
        result.filesChanged.push({
          path: 'package.json',
          action: 'modified',
          content: JSON.stringify(packageJson, null, 2)
        });
      }

      result.success = result.errors.length === 0;
      result.message = 'Jest testing setup complete! Run `npm test` to verify your tests work.';
      return result;

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      return result;
    }
  },

  getPreview: async (config: ScaffoldConfig): Promise<FileChange[]> => {
    const changes: FileChange[] = [];

    // Preview jest.config.js
    const jestConfig = generateJestConfig('nodejs', config.features || [], config.includeCoverage, false);

    changes.push({
      path: 'jest.config.js',
      action: 'created',
      content: jestConfig
    });

    // Preview test setup file
    if (config.includeSetupFiles) {
      changes.push({
        path: 'test/setup.ts',
        action: 'created',
        content: generateSetupFile('nodejs', config.features || [])
      });
    }

    // Preview example test
    changes.push({
      path: 'test/example.test.ts',
      action: 'created',
      content: generateExampleTest('nodejs')
    });

    return changes;
  }
};

// Helper functions
function generateJestConfig(framework: Framework, features: string[], includeCoverage: boolean, isESMProject: boolean = false): string {
  const exportSyntax = isESMProject ? 'export default {' : 'module.exports = {';
  const lines = [exportSyntax];
  
  // Basic configuration
  lines.push('  testEnvironment: \'node\',');
  lines.push('  roots: [\'<rootDir>/test\', \'<rootDir>/src\'],');
  lines.push('  testMatch: [\'**/__tests__/**/*.ts\', \'**/?(*.)+(spec|test).ts\'],');
  lines.push('  transform: {');
  lines.push('    \'^.+\\.ts$\': \'ts-jest\',');
  lines.push('  },');
  
  // Framework-specific configuration
  switch (framework) {
    case 'nextjs':
      lines.push('  testEnvironment: \'jsdom\',');
      lines.push('  setupFilesAfterEnv: [\'<rootDir>/test/setup.ts\'],');
      lines.push('  moduleNameMapping: {');
      lines.push('    \'^@/(.*)$\': \'<rootDir>/src/$1\',');
      lines.push('  },');
      break;
      
    case 'react-vite':
    case 'react-cra':
      lines.push('  testEnvironment: \'jsdom\',');
      lines.push('  setupFilesAfterEnv: [\'<rootDir>/test/setup.ts\'],');
      lines.push('  moduleNameMapping: {');
      lines.push('    \'^@/(.*)$\': \'<rootDir>/src/$1\',');
      lines.push('  },');
      break;
      
    case 'vue':
      lines.push('  testEnvironment: \'jsdom\',');
      lines.push('  setupFilesAfterEnv: [\'<rootDir>/test/setup.ts\'],');
      lines.push('  moduleNameMapping: {');
      lines.push('    \'^@/(.*)$\': \'<rootDir>/src/$1\',');
      lines.push('  },');
      break;
      
    case 'angular':
      lines.push('  testEnvironment: \'jsdom\',');
      lines.push('  setupFilesAfterEnv: [\'<rootDir>/test/setup.ts\'],');
      lines.push('  moduleNameMapping: {');
      lines.push('    \'^@/(.*)$\': \'<rootDir>/src/$1\',');
      lines.push('  },');
      break;
      
    case 'svelte':
      lines.push('  testEnvironment: \'jsdom\',');
      lines.push('  setupFilesAfterEnv: [\'<rootDir>/test/setup.ts\'],');
      lines.push('  moduleNameMapping: {');
      lines.push('    \'^@/(.*)$\': \'<rootDir>/src/$1\',');
      lines.push('  },');
      break;
      
    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      lines.push('  testEnvironment: \'node\',');
      break;
  }
  
  // Coverage configuration
  if (includeCoverage) {
    lines.push('  collectCoverage: true,');
    lines.push('  coverageDirectory: \'coverage\',');
    lines.push('  coverageReporters: [\'text\', \'lcov\', \'html\'],');
    lines.push('  collectCoverageFrom: [');
    lines.push('    \'src/**/*.{ts,tsx,js,jsx}\',');
    lines.push('    \'!src/**/*.d.ts\',');
    lines.push('    \'!src/**/*.stories.{ts,tsx,js,jsx}\',');
    lines.push('  ],');
  }
  
  lines.push('};');
  
  return lines.join('\n');
}

function generateSetupFile(framework: Framework, features: string[]): string {
  const lines = [];
  
  // Framework-specific setup
  switch (framework) {
    case 'nextjs':
    case 'react-vite':
    case 'react-cra':
      lines.push('import \'@testing-library/jest-dom\';');
      lines.push('');
      lines.push('// Mock Next.js router');
      lines.push('jest.mock(\'next/router\', () => ({');
      lines.push('  useRouter() {');
      lines.push('    return {');
      lines.push('      route: \'/\',');
      lines.push('      pathname: \'/\',');
      lines.push('      query: {},');
      lines.push('      asPath: \'/\',');
      lines.push('      push: jest.fn(),');
      lines.push('      pop: jest.fn(),');
      lines.push('      reload: jest.fn(),');
      lines.push('      back: jest.fn(),');
      lines.push('      prefetch: jest.fn().mockResolvedValue(undefined),');
      lines.push('      beforePopState: jest.fn(),');
      lines.push('      events: {');
      lines.push('        on: jest.fn(),');
      lines.push('        off: jest.fn(),');
      lines.push('        emit: jest.fn(),');
      lines.push('      },');
      lines.push('      isFallback: false,');
      lines.push('    };');
      lines.push('  },');
      lines.push('}));');
      break;
      
    case 'vue':
      lines.push('import { config } from \'@vue/test-utils\';');
      lines.push('');
      lines.push('// Global test configuration');
      lines.push('config.global.stubs = {');
      lines.push('  transition: false,');
      lines.push('  \'transition-group\': false,');
      lines.push('};');
      break;
      
    case 'angular':
      lines.push('import \'zone.js/dist/zone-testing\';');
      lines.push('import { getTestBed } from \'@angular/core/testing\';');
      lines.push('import { BrowserDynamicTestingModule, platformBrowserDynamicTesting } from \'@angular/platform-browser-dynamic/testing\';');
      lines.push('');
      lines.push('getTestBed().initTestEnvironment(');
      lines.push('  BrowserDynamicTestingModule,');
      lines.push('  platformBrowserDynamicTesting()');
      lines.push(');');
      break;
      
    case 'svelte':
      lines.push('// Svelte testing setup');
      lines.push('import { vi } from \'vitest\';');
      lines.push('');
      lines.push('// Mock Svelte stores');
      lines.push('vi.mock(\'svelte/store\', () => ({');
      lines.push('  writable: vi.fn(),');
      lines.push('  readable: vi.fn(),');
      lines.push('  derived: vi.fn(),');
      lines.push('}));');
      break;
      
    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      lines.push('// Node.js testing setup');
      lines.push('import { jest } from \'@jest/globals\';');
      lines.push('');
      lines.push('// Global test utilities');
      lines.push('global.console = {');
      lines.push('  ...console,');
      lines.push('  log: jest.fn(),');
      lines.push('  error: jest.fn(),');
      lines.push('  warn: jest.fn(),');
      lines.push('  info: jest.fn(),');
      lines.push('};');
      break;
  }
  
  // Feature-specific setup
  if (features.includes('matchers')) {
    lines.push('');
    lines.push('// Custom matchers');
    lines.push('expect.extend({');
    lines.push('  toBeValidEmail(received) {');
    lines.push('    const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;');
    lines.push('    const pass = emailRegex.test(received);');
    lines.push('    return {');
    lines.push('      message: () => `expected ${received} ${pass ? \'not \' : \'\'}to be a valid email`,');
    lines.push('      pass,');
    lines.push('    };');
    lines.push('  },');
    lines.push('});');
  }
  
  return lines.join('\n');
}

function generateExampleTest(framework: Framework): string {
  switch (framework) {
    case 'nextjs':
    case 'react-vite':
    case 'react-cra':
      return `import { render, screen } from '@testing-library/react';
import { expect, test, describe } from '@jest/globals';

// Example component test
describe('Example Component', () => {
  test('renders without crashing', () => {
    render(<div>Hello World</div>);
    expect(screen.getByText('Hello World')).toBeInTheDocument();
  });

  test('handles user interaction', () => {
    render(<button>Click me</button>);
    expect(screen.getByRole('button')).toBeInTheDocument();
  });
});

// Example utility function test
describe('Utility Functions', () => {
  test('adds two numbers correctly', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(2, 3)).toBe(5);
  });

  test('handles edge cases', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(0, 0)).toBe(0);
    expect(add(-1, 1)).toBe(0);
  });
});`;

    case 'vue':
      return `import { mount } from '@vue/test-utils';
import { expect, test, describe } from '@jest/globals';

// Example component test
describe('Example Component', () => {
  test('renders without crashing', () => {
    const wrapper = mount({
      template: '<div>Hello World</div>'
    });
    expect(wrapper.text()).toBe('Hello World');
  });

  test('handles user interaction', () => {
    const wrapper = mount({
      template: '<button @click="handleClick">Click me</button>',
      methods: {
        handleClick() {
          this.$emit('click');
        }
      }
    });
    expect(wrapper.find('button').exists()).toBe(true);
  });
});

// Example utility function test
describe('Utility Functions', () => {
  test('adds two numbers correctly', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(2, 3)).toBe(5);
  });

  test('handles edge cases', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(0, 0)).toBe(0);
    expect(add(-1, 1)).toBe(0);
  });
});`;

    case 'angular':
      return `import { ComponentFixture, TestBed } from '@angular/core/testing';
import { expect, test, describe } from '@jest/globals';

// Example component test
describe('Example Component', () => {
  test('renders without crashing', () => {
    expect(true).toBe(true);
  });

  test('handles user interaction', () => {
    expect(true).toBe(true);
  });
});

// Example utility function test
describe('Utility Functions', () => {
  test('adds two numbers correctly', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(2, 3)).toBe(5);
  });

  test('handles edge cases', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(0, 0)).toBe(0);
    expect(add(-1, 1)).toBe(0);
  });
});`;

    case 'svelte':
      return `import { render } from '@testing-library/svelte';
import { expect, test, describe } from '@jest/globals';

// Example component test
describe('Example Component', () => {
  test('renders without crashing', () => {
    const { getByText } = render({ target: document.body, props: {} });
    expect(true).toBe(true);
  });

  test('handles user interaction', () => {
    expect(true).toBe(true);
  });
});

// Example utility function test
describe('Utility Functions', () => {
  test('adds two numbers correctly', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(2, 3)).toBe(5);
  });

  test('handles edge cases', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(0, 0)).toBe(0);
    expect(add(-1, 1)).toBe(0);
  });
});`;

    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      return `import { expect, test, describe } from '@jest/globals';

// Example utility function test
describe('Utility Functions', () => {
  test('adds two numbers correctly', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(2, 3)).toBe(5);
  });

  test('handles edge cases', () => {
    const add = (a: number, b: number) => a + b;
    expect(add(0, 0)).toBe(0);
    expect(add(-1, 1)).toBe(0);
  });
});

// Example async function test
describe('Async Functions', () => {
  test('handles async operations', async () => {
    const asyncAdd = async (a: number, b: number) => {
      return new Promise(resolve => {
        setTimeout(() => resolve(a + b), 100);
      });
    };

    const result = await asyncAdd(2, 3);
    expect(result).toBe(5);
  });
});

// Example mock test
describe('Mock Functions', () => {
  test('mocks function calls', () => {
    const mockFn = jest.fn();
    mockFn('test');
    
    expect(mockFn).toHaveBeenCalledWith('test');
    expect(mockFn).toHaveBeenCalledTimes(1);
  });
});`;
  }
}

function generateTestUtils(framework: Framework): string {
  const lines = [];
  
  lines.push('import { expect } from \'@jest/globals\';');
  lines.push('');
  
  // Framework-specific utilities
  switch (framework) {
    case 'nextjs':
    case 'react-vite':
    case 'react-cra':
      lines.push('import { render, RenderOptions } from \'@testing-library/react\';');
      lines.push('import { ReactElement } from \'react\';');
      lines.push('');
      lines.push('// Custom render function with providers');
      lines.push('const customRender = (');
      lines.push('  ui: ReactElement,');
      lines.push('  options?: Omit<RenderOptions, \'wrapper\'>');
      lines.push(') => {');
      lines.push('  return render(ui, {');
      lines.push('    ...options,');
      lines.push('  });');
      lines.push('};');
      lines.push('');
      lines.push('export * from \'@testing-library/react\';');
      lines.push('export { customRender as render };');
      break;
      
    case 'vue':
      lines.push('import { mount, MountingOptions } from \'@vue/test-utils\';');
      lines.push('import { Component } from \'vue\';');
      lines.push('');
      lines.push('// Custom mount function with global config');
      lines.push('const customMount = (');
      lines.push('  component: Component,');
      lines.push('  options?: MountingOptions<any>');
      lines.push(') => {');
      lines.push('  return mount(component, {');
      lines.push('    ...options,');
      lines.push('  });');
      lines.push('};');
      lines.push('');
      lines.push('export * from \'@vue/test-utils\';');
      lines.push('export { customMount as mount };');
      break;
      
    case 'angular':
      lines.push('import { TestBed } from \'@angular/core/testing\';');
      lines.push('');
      lines.push('// Custom test bed configuration');
      lines.push('export const configureTestBed = (config: any) => {');
      lines.push('  TestBed.configureTestingModule(config);');
      lines.push('};');
      break;
      
    case 'svelte':
      lines.push('import { render } from \'@testing-library/svelte\';');
      lines.push('');
      lines.push('// Custom render function');
      lines.push('export const customRender = (component: any, props: any = {}) => {');
      lines.push('  return render(component, { props });');
      lines.push('};');
      break;
      
    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      lines.push('// Node.js test utilities');
      lines.push('export const createMockRequest = (overrides: any = {}) => ({');
      lines.push('  body: {},');
      lines.push('  params: {},');
      lines.push('  query: {},');
      lines.push('  headers: {},');
      lines.push('  ...overrides,');
      lines.push('});');
      lines.push('');
      lines.push('export const createMockResponse = () => {');
      lines.push('  const res: any = {};');
      lines.push('  res.status = jest.fn().mockReturnValue(res);');
      lines.push('  res.json = jest.fn().mockReturnValue(res);');
      lines.push('  res.send = jest.fn().mockReturnValue(res);');
      lines.push('  res.end = jest.fn().mockReturnValue(res);');
      lines.push('  return res;');
      lines.push('};');
      break;
  }
  
  // Common utilities
  lines.push('');
  lines.push('// Common test utilities');
  lines.push('export const waitFor = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));');
  lines.push('');
  lines.push('export const createMockFn = () => jest.fn();');
  lines.push('');
  lines.push('export const expectToBeCalledWith = (mockFn: jest.Mock, ...args: any[]) => {');
  lines.push('  expect(mockFn).toHaveBeenCalledWith(...args);');
  lines.push('};');
  
  return lines.join('\n');
}

function generateMockUtils(framework: Framework): string {
  const lines = [];
  
  lines.push('import { jest } from \'@jest/globals\';');
  lines.push('');
  
  // Framework-specific mocks
  switch (framework) {
    case 'nextjs':
      lines.push('// Next.js mocks');
      lines.push('export const mockNextRouter = () => ({');
      lines.push('  route: \'/\',');
      lines.push('  pathname: \'/\',');
      lines.push('  query: {},');
      lines.push('  asPath: \'/\',');
      lines.push('  push: jest.fn(),');
      lines.push('  pop: jest.fn(),');
      lines.push('  reload: jest.fn(),');
      lines.push('  back: jest.fn(),');
      lines.push('  prefetch: jest.fn().mockResolvedValue(undefined),');
      lines.push('  beforePopState: jest.fn(),');
      lines.push('  events: {');
      lines.push('    on: jest.fn(),');
      lines.push('    off: jest.fn(),');
      lines.push('    emit: jest.fn(),');
      lines.push('  },');
      lines.push('  isFallback: false,');
      lines.push('});');
      lines.push('');
      lines.push('export const mockNextImage = () => ({');
      lines.push('  src: \'test-image.jpg\',');
      lines.push('  alt: \'test image\',');
      lines.push('  width: 100,');
      lines.push('  height: 100,');
      lines.push('});');
      break;
      
    case 'react-vite':
    case 'react-cra':
      lines.push('// React mocks');
      lines.push('export const mockReactComponent = (name: string) => {');
      lines.push('  const MockComponent = () => <div data-testid={name}>{name}</div>;');
      lines.push('  MockComponent.displayName = name;');
      lines.push('  return MockComponent;');
      lines.push('};');
      break;
      
    case 'vue':
      lines.push('// Vue mocks');
      lines.push('export const mockVueComponent = (name: string) => ({');
      lines.push('  name,');
      lines.push('  template: `<div data-testid="${name}">${name}</div>`,');
      lines.push('});');
      break;
      
    case 'angular':
      lines.push('// Angular mocks');
      lines.push('export const mockAngularService = (name: string) => ({');
      lines.push('  name,');
      lines.push('  mockMethod: jest.fn(),');
      lines.push('});');
      break;
      
    case 'svelte':
      lines.push('// Svelte mocks');
      lines.push('export const mockSvelteStore = (initialValue: any) => ({');
      lines.push('  subscribe: jest.fn(),');
      lines.push('  set: jest.fn(),');
      lines.push('  update: jest.fn(),');
      lines.push('});');
      break;
      
    case 'express':
    case 'fastify':
    case 'nodejs':
    default:
      lines.push('// Node.js mocks');
      lines.push('export const mockExpressRequest = (overrides: any = {}) => ({');
      lines.push('  body: {},');
      lines.push('  params: {},');
      lines.push('  query: {},');
      lines.push('  headers: {},');
      lines.push('  method: \'GET\',');
      lines.push('  url: \'/\',');
      lines.push('  ...overrides,');
      lines.push('});');
      lines.push('');
      lines.push('export const mockExpressResponse = () => {');
      lines.push('  const res: any = {};');
      lines.push('  res.status = jest.fn().mockReturnValue(res);');
      lines.push('  res.json = jest.fn().mockReturnValue(res);');
      lines.push('  res.send = jest.fn().mockReturnValue(res);');
      lines.push('  res.end = jest.fn().mockReturnValue(res);');
      lines.push('  res.setHeader = jest.fn().mockReturnValue(res);');
      lines.push('  res.getHeader = jest.fn();');
      lines.push('  return res;');
      lines.push('};');
      break;
  }
  
  // Common mocks
  lines.push('');
  lines.push('// Common mocks');
  lines.push('export const mockFetch = () => {');
  lines.push('  const mockResponse = {');
  lines.push('    ok: true,');
  lines.push('    status: 200,');
  lines.push('    json: jest.fn().mockResolvedValue({}),');
  lines.push('    text: jest.fn().mockResolvedValue(\'\'),');
  lines.push('  };');
  lines.push('  return jest.fn().mockResolvedValue(mockResponse);');
  lines.push('};');
  lines.push('');
  lines.push('export const mockLocalStorage = () => {');
  lines.push('  const store: any = {};');
  lines.push('  return {');
  lines.push('    getItem: jest.fn((key: string) => store[key] || null),');
  lines.push('    setItem: jest.fn((key: string, value: string) => {');
  lines.push('      store[key] = value;');
  lines.push('    }),');
  lines.push('    removeItem: jest.fn((key: string) => {');
  lines.push('      delete store[key];');
  lines.push('    }),');
  lines.push('    clear: jest.fn(() => {');
  lines.push('      Object.keys(store).forEach(key => delete store[key]);');
  lines.push('    }),');
  lines.push('  };');
  lines.push('};');
  
  return lines.join('\n');
}

// Standalone usage functions for direct npm package usage
// Note: Requires @scaffit/core to be installed
export async function setupJest(options: {
  includeCoverage?: boolean;
  includeSetupFiles?: boolean;
  features?: string[];
  projectRoot?: string;
} = {}): Promise<InstallResult> {
  const config: ScaffoldConfig = {
    includeCoverage: options.includeCoverage ?? true,
    includeSetupFiles: options.includeSetupFiles ?? true,
    features: options.features ?? ['typescript', 'eslint', 'mocks', 'utils'],
    projectInfo: {
      rootDir: options.projectRoot || process.cwd()
    }
  };
  
  return scaffold.install(config);
}

export async function previewJest(options: {
  includeCoverage?: boolean;
  includeSetupFiles?: boolean;
  features?: string[];
} = {}): Promise<FileChange[]> {
  const config: ScaffoldConfig = {
    includeCoverage: options.includeCoverage ?? true,
    includeSetupFiles: options.includeSetupFiles ?? true,
    features: options.features ?? ['typescript', 'eslint', 'mocks', 'utils']
  };
  
  return scaffold.getPreview!(config);
}

export default scaffold;